import React, { useState, useRef, useEffect } from "react";
import {
  useGetAllUsersQuery,
  useDeleteUserMutation,
} from "../../services/api";
import UserForm from "../components/UserForm";
import ErrorMessage from "../../Components/common/ErrorMessage";
import Loading from "../../Components/common/Loading";
import "./Users.css";

// SVG Icons
const IconSearch = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconEdit = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconDelete = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconPlus = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// Role level mapping
const ROLE_LEVELS = {
  1: "User",
  2: "User Host",
  3: "Staff",
  4: "Admin",
};

export default function Users() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const inputRef = useRef(null);

  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useGetAllUsersQuery({
    page,
    limit: 10,
    search: search || undefined,
    role: roleFilter || undefined,
    isActive: isActiveFilter !== "" ? isActiveFilter === "true" : undefined,
  });

  const [deleteUser] = useDeleteUserMutation();

  // Keyboard shortcut Ctrl+K for search
  useEffect(() => {
    const onKey = (e) => {
      const isCtrlK = (e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === "k";
      if (isCtrlK) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleCloseForm = () => {
    setShowUserForm(false);
    setEditingUser(null);
    refetch();
  };

  const handleDeleteUser = async (userId, userName) => {
    if (
      window.confirm(
        `Are you sure you want to delete user "${userName}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteUser(userId).unwrap();
        setShowUserForm(false); // Close modal after successful deletion
        setEditingUser(null);
        refetch();
      } catch (err) {
        console.error("Failed to delete user:", err);
        alert("Failed to delete user. Please try again.");
      }
    }
  };

  const users = usersData?.users || [];
  const pagination = usersData?.pagination || {};

  if (isLoading && !users.length) {
    return <Loading fullPage={false} />;
  }

  return (
    <div className="users-page">
      {/* Header */}
      <div className="users-header">
        <div>
          <h1 className="admin-h1">User Management</h1>
          <p className="users-subtitle">
            Manage users, roles, and permissions
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateUser}>
          <IconPlus /> Create User
        </button>
      </div>

      {/* Filters */}
      <div className="users-filters">
        <div className="users-count">
          Showing {users.length} of {pagination.total || 0} users
        </div>

        <div className="users-filter-controls">
          {/* Search */}
          <div className="users-search">
            <IconSearch />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <span className="key-hint">Ctrl K</span>
          </div>

          {/* Role Filter */}
          <select
            className="users-select"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Roles</option>
            {Object.entries(ROLE_LEVELS).map(([level, name]) => (
              <option key={level} value={level}>
                {name}
              </option>
            ))}
          </select>

          {/* Active Filter */}
          <select
            className="users-select"
            value={isActiveFilter}
            onChange={(e) => {
              setIsActiveFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && <ErrorMessage error={error} />}

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Company</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" className="users-empty">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="user-name-cell">
                      <div className="user-avatar">
                        {user.firstName?.[0]}
                        {user.lastName?.[0]}
                      </div>
                      <div>
                        <div className="user-fullname">
                          {user.firstName} {user.lastName}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge role-level-${user.role?.level}`}>
                      {ROLE_LEVELS[user.role?.level] || "Unknown"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        user.isActive ? "status-active" : "status-inactive"
                      }`}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{user.company?.name || "-"}</td>
                  <td>
                    <div className="users-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleEditUser(user)}
                        title="Edit user"
                      >
                        <IconEdit />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="users-pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= pagination.totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* User Form Modal */}
      {showUserForm && (
        <UserForm
          user={editingUser}
          onClose={handleCloseForm}
          onDelete={handleDeleteUser}
        />
      )}
    </div>
  );
}
